import { Component } from 'react';
import { Typography } from '../../../../../components/typography/typography';
import { Button } from '../../../../../components/form/button';
import Icon from '../../../../../components/icons/icon';

interface ProductItemErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ProductItemErrorBoundaryProps {
  children: React.ReactNode;
  onRetry?: () => void;
}

export class ProductItemErrorBoundary extends Component<ProductItemErrorBoundaryProps, ProductItemErrorBoundaryState> {
  constructor(props: ProductItemErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ProductItemErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('OrderProduct Error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    this.props.onRetry?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="order-contract-wrap min-w-[0] my-[8px] border border-solid border-red-200 rounded-lg bg-red-50 p-4">
          <div className="flex items-center gap-3">
            <Icon icon="CircleExclamation" size="medium" className="text-red-600" />
            <div className="flex-1">
              <Typography
                type="text-sm"
                weight="semi-bold"
                className="text-red-800 mb-1"
                dataCy="product-item-error-boundary-title"
              >
                Product Display Error
              </Typography>
              <Typography
                type="text-xs"
                weight="regular"
                className="text-red-700 mb-3"
                dataCy="product-item-error-boundary-description"
              >
                Something went wrong displaying this product.
              </Typography>
              <Button
                className="button button-secondary !text-xs !py-1 !px-2"
                onClick={this.handleRetry}
                dataCy="product-item-error-boundary-retry"
              >
                <Icon icon="Refresh" size="small" className="mr-1" />
                Retry
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto order-contract-wrap min-w-[0] my-[8px] border border-solid border-grey-200 rounded-lg flex flex-wrap">
        {this.props.children}
      </div>
    );
  }
}
